import { Skeleton } from 'primereact/skeleton';
import { ShowerHead, Sprout, Leaf } from 'lucide-react';
import { formatNumber } from '@agro1desenvolvimento/utils-js';
import type { Talhao } from '../../../@types';
import type { TalhaoAlerta } from '../../../services/alertas-talhao';
import {
  CLASSIFICACAO_META,
  type ClassificacaoKey,
  formatTipo,
  formatPlantioTooltip,
  toTitleCase,
} from './alertas-utils';

export const GLEBA_TOOLTIP_TARGETS = {
  infoIcon: '.gleba-info-icon',
  badge: '.gleba-classif-badge',
} as const;

const CLASSIFICACAO_NIVEIS: ClassificacaoKey[] = [
  'alerta',
  'controle',
  'equilibrio',
  'neutro',
];

type GlebaItemProps = {
  gleba: Talhao;
  alerta: TalhaoAlerta | undefined;
  alertasLoading: boolean;
  alertasError: boolean;
};

const GlebaItem = ({
  gleba,
  alerta,
  alertasLoading,
  alertasError,
}: GlebaItemProps) => {
  const hasBadges = CLASSIFICACAO_NIVEIS.some(
    (nivel) => (alerta?.[nivel].length ?? 0) > 0,
  );

  return (
    <div className="gleba-item">
      <div className="gleba-item-left">
        <span className="gleba-item-nome">
          {gleba.descricao}
          <span className="gleba-item-area">
            {' '}
            ({formatNumber(gleba.areaTotal)} ha)
          </span>
        </span>

        {alertasLoading && (
          <div className="gleba-item-skeleton">
            <Skeleton width="70%" height="0.75rem" />
          </div>
        )}

        {!alertasLoading && alertasError && (
          <span className="gleba-item-error-text">
            Erro ao carregar alertas
          </span>
        )}

        {!alertasLoading && !alertasError && alerta && (
          <>
            <div className="gleba-info-row">
              <i
                className="pi pi-calendar gleba-info-icon"
                data-pr-tooltip="Última visita"
                data-pr-position="top"
              />
              {alerta.diasDesdeAUltimaAcao != null ? (
                <span>há {alerta.diasDesdeAUltimaAcao} dias</span>
              ) : (
                <span className="gleba-info-sem-dados">sem visitas</span>
              )}
              <ShowerHead
                className="gleba-info-icon gleba-info-icon-lucide"
                data-pr-tooltip="Última pulverização"
                data-pr-position="top"
              />
              {alerta.ultimaPulverizacao != null ? (
                <span>há {alerta.ultimaPulverizacao} dias</span>
              ) : (
                <span className="gleba-info-sem-dados">não pulverizado</span>
              )}
            </div>

            {alerta.estadios && alerta.estadios.length > 0 && (
              <div className="gleba-estadios">
                {alerta.estadios.map((estadio) => {
                  const plantioTooltip = formatPlantioTooltip(
                    estadio.plantio?.inicio,
                    estadio.plantio?.fim,
                  );
                  return (
                    <div
                      key={`${estadio.atividade.id}-${estadio.estadio.id}`}
                      className="gleba-estadio-row"
                    >
                      <Sprout className="gleba-info-icon gleba-info-icon-lucide" />
                      <span className="gleba-estadio-atividade">
                        {toTitleCase(estadio.atividade.descricao)}
                      </span>
                      <Leaf className="gleba-info-icon gleba-info-icon-lucide gleba-info-icon-sm" />
                      <span>{estadio.estadio.abreviatura}</span>
                      {plantioTooltip && (
                        <span className="gleba-estadio-plantio">
                          {plantioTooltip}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="gleba-item-right">
        {gleba.dataBaixa && (
          <span className="gleba-badge gleba-badge--baixado">Baixado</span>
        )}
        {!alertasLoading && !alertasError && alerta && hasBadges && (
          <div className="gleba-classificacao-badges">
            {CLASSIFICACAO_NIVEIS.flatMap((nivel) =>
              alerta[nivel].map((classificacao) => {
                const meta = CLASSIFICACAO_META[nivel];
                const hints = classificacao.hint?.length
                  ? classificacao.hint.join(' · ')
                  : '';
                return (
                  <span
                    key={`${nivel}-${classificacao.tipo}-${classificacao.gleba}`}
                    className="gleba-classif-badge"
                    data-pr-tooltip={
                      hints
                        ? `${meta.label}: ${hints}`
                        : `${meta.label} · ${formatTipo(classificacao.tipo)}`
                    }
                    data-pr-position="left"
                    style={{
                      backgroundColor: meta.bgColor,
                      color: meta.textColor,
                    }}
                  >
                    {formatTipo(classificacao.tipo)}
                  </span>
                );
              }),
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlebaItem;
